/**
 * Site Data Tab — Hydrogeology, soil, aquifer, and flow analysis display.
 *
 * Pure display component showing SSURGO/3DEP/NWIS/TWDB data for the site.
 */
import { FadeIn } from '../animations/FadeIn';
import type { ContaminantInput } from '../../api/polygon';

interface ToolkitData {
  soil_type: string;
  aquifer_k_ft_day: number;
  aquifer_porosity: number;
  depth_to_water_ft: number;
  land_use: string;
  clay_pct: number;
  sand_pct: number;
  organic_matter_pct: number;
  organic_carbon_fraction: number;
  ksat_in_hr: number;
  hydrologic_group: string;
  water_level_date: string;
  water_elevation_ft: number;
  ground_elevation_ft: number;
  hydraulic_gradient: number;
  aquifer_type: string;
  aquifer_name: string;
  aquifer_code: string;
  groundwater_velocity_ft_day: number;
  data_sources: string[];
  confidence_score: number;
}

interface PhysicsData {
  darcy_velocity_ft_day: number;
  seepage_velocity_ft_day: number;
  annual_migration_ft: number;
  flow_direction_degrees: number;
}

interface SiteDataTabProps {
  toolkitData: ToolkitData;
  physics: PhysicsData | null;
  contaminants?: ContaminantInput[];
}

export function SiteDataTab({ toolkitData, physics, contaminants }: SiteDataTabProps) {
  return (
    <FadeIn delay={200}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Soil Properties (SSURGO) */}
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">SOIL PROPERTIES</h3>
            <span className="text-[10px] text-mono-600 bg-mono-800 px-2 py-0.5 rounded">SSURGO</span>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Soil Type</span>
              <span className="text-mono-200 text-xs truncate max-w-[180px]" title={toolkitData.soil_type}>{toolkitData.soil_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Sand Content</span>
              <span className="text-mono-200 text-xs">{toolkitData.sand_pct > 0 ? `${toolkitData.sand_pct.toFixed(1)}%` : '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Clay Content</span>
              <span className="text-mono-200 text-xs">{toolkitData.clay_pct > 0 ? `${toolkitData.clay_pct.toFixed(1)}%` : '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Organic Matter</span>
              <span className="text-mono-200 text-xs">{toolkitData.organic_matter_pct > 0 ? `${toolkitData.organic_matter_pct.toFixed(2)}%` : '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Organic Carbon (foc)</span>
              <span className="text-mono-200 text-xs">{toolkitData.organic_carbon_fraction > 0 ? toolkitData.organic_carbon_fraction.toFixed(4) : '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Ksat</span>
              <span className="text-mono-200 text-xs">{toolkitData.ksat_in_hr > 0 ? `${toolkitData.ksat_in_hr.toFixed(2)} in/hr` : '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Hydrologic Group</span>
              <span className="text-mono-200 text-xs font-mono">{toolkitData.hydrologic_group || '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Land Use</span>
              <span className="text-mono-200 text-xs">{toolkitData.land_use}</span>
            </div>
          </div>
        </div>

        {/* Elevation & Water Levels (3DEP + NWIS) */}
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">ELEVATION & WATER</h3>
            <span className="text-[10px] text-mono-600 bg-mono-800 px-2 py-0.5 rounded">3DEP/NWIS</span>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Ground Elevation</span>
              <span className="text-mono-200 text-xs">{toolkitData.ground_elevation_ft > 0 ? `${toolkitData.ground_elevation_ft.toFixed(1)} ft MSL` : '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Water Table Elev</span>
              <span className="text-mono-200 text-xs">{toolkitData.water_elevation_ft > 0 ? `${toolkitData.water_elevation_ft.toFixed(1)} ft MSL` : '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Depth to Water</span>
              <span className="text-cyan-400 text-xs font-medium">{toolkitData.depth_to_water_ft} ft</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Hydraulic Gradient</span>
              <span className="text-mono-200 text-xs">{toolkitData.hydraulic_gradient > 0 ? toolkitData.hydraulic_gradient.toFixed(4) : '--'}</span>
            </div>
            {toolkitData.water_level_date && (
              <div className="flex justify-between">
                <span className="text-mono-500 text-xs">Water Level Date</span>
                <span className="text-mono-400 text-xs">{new Date(toolkitData.water_level_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Aquifer Properties */}
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">AQUIFER PROPERTIES</h3>
            <span className="text-[10px] text-mono-600 bg-mono-800 px-2 py-0.5 rounded">TWDB/NWIS</span>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Aquifer Name</span>
              <span className="text-mono-200 text-xs truncate max-w-[180px]" title={toolkitData.aquifer_name}>{toolkitData.aquifer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Aquifer Type</span>
              <span className="text-mono-200 text-xs capitalize">{toolkitData.aquifer_type}</span>
            </div>
            {toolkitData.aquifer_code && (
              <div className="flex justify-between">
                <span className="text-mono-500 text-xs">Aquifer Code</span>
                <span className="text-mono-400 text-xs font-mono">{toolkitData.aquifer_code}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Hydraulic K</span>
              <span className="text-cyan-400 text-xs font-medium">{toolkitData.aquifer_k_ft_day.toFixed(2)} ft/day</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mono-500 text-xs">Effective Porosity</span>
              <span className="text-cyan-400 text-xs font-medium">{(toolkitData.aquifer_porosity * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Physics/Flow Data */}
        {physics && (
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">FLOW ANALYSIS</h3>
              <span className="text-[10px] text-mono-600 bg-mono-800 px-2 py-0.5 rounded">CALCULATED</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-mono-500 text-xs">Darcy Velocity</span>
                <span className="text-mono-200 text-xs">{physics.darcy_velocity_ft_day.toFixed(4)} ft/day</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mono-500 text-xs">Seepage Velocity</span>
                <span className="text-cyan-400 text-xs font-medium">{physics.seepage_velocity_ft_day.toFixed(4)} ft/day</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mono-500 text-xs">GW Velocity</span>
                <span className="text-mono-200 text-xs">{toolkitData.groundwater_velocity_ft_day.toFixed(4)} ft/day</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mono-500 text-xs">Annual Migration</span>
                <span className="text-orange-400 text-xs font-medium">{physics.annual_migration_ft.toFixed(1)} ft/yr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-mono-500 text-xs">Flow Direction</span>
                <span className="text-mono-200 text-xs">{physics.flow_direction_degrees.toFixed(0)}&deg; from N</span>
              </div>
              {/* Flow direction visual */}
              <div className="mt-2 flex items-center justify-center">
                <div className="relative w-16 h-16 border border-mono-700 rounded-full">
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 text-[8px] text-mono-500">N</div>
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[8px] text-mono-500">S</div>
                  <div className="absolute left-1 top-1/2 -translate-y-1/2 text-[8px] text-mono-500">W</div>
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 text-[8px] text-mono-500">E</div>
                  <div
                    className="absolute top-1/2 left-1/2 w-6 h-0.5 bg-cyan-500 origin-left"
                    style={{ transform: `translate(-50%, -50%) rotate(${physics.flow_direction_degrees - 90}deg)` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-l-4 border-l-cyan-500 border-y-2 border-y-transparent" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Quality */}
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">DATA QUALITY</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-mono-500 text-xs">Confidence Score</span>
              <div className="flex items-center gap-2">
                <div className="w-20 h-1.5 bg-mono-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      toolkitData.confidence_score >= 0.8 ? 'bg-green-500' :
                      toolkitData.confidence_score >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${toolkitData.confidence_score * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  toolkitData.confidence_score >= 0.8 ? 'text-green-400' :
                  toolkitData.confidence_score >= 0.5 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {(toolkitData.confidence_score * 100).toFixed(0)}%
                </span>
              </div>
            </div>
            <div>
              <span className="text-mono-500 text-xs block mb-2">Data Sources</span>
              <div className="flex flex-wrap gap-1">
                {toolkitData.data_sources && toolkitData.data_sources.length > 0 ? (
                  toolkitData.data_sources.map((source, idx) => (
                    <span key={idx} className="text-[10px] bg-mono-800 text-mono-300 px-2 py-0.5 rounded">
                      {source}
                    </span>
                  ))
                ) : (
                  <>
                    <span className="text-[10px] bg-mono-800 text-mono-300 px-2 py-0.5 rounded">SSURGO</span>
                    <span className="text-[10px] bg-mono-800 text-mono-300 px-2 py-0.5 rounded">3DEP</span>
                    <span className="text-[10px] bg-mono-800 text-mono-300 px-2 py-0.5 rounded">NWIS</span>
                  </>
                )}
              </div>
            </div>
            <div className="pt-2 border-t border-mono-800">
              <span className="text-mono-600 text-[10px] block">
                Data sourced from USGS APIs (SSURGO soil properties, 3DEP elevation, NWIS groundwater levels) and Texas TWDB aquifer database.
              </span>
            </div>
          </div>
        </div>

        {/* Contaminants */}
        {contaminants && contaminants.length > 0 && (
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider mb-4 font-orbitron">
              CONTAMINANTS ({contaminants.length})
            </h3>
            <div className="space-y-3">
              {contaminants.map((contaminant, idx) => {
                const concUgL = contaminant.concentration_ug_l || 0;
                const mcl = contaminant.mcl || 5;
                const exceedance = concUgL / mcl;
                const exceedanceColor = exceedance > 100 ? 'text-red-400' : exceedance > 10 ? 'text-orange-400' : exceedance > 1 ? 'text-yellow-400' : 'text-green-400';
                const barWidth = Math.min(100, (exceedance / 100) * 100);

                return (
                  <div key={idx} className="p-2 bg-mono-950 rounded">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-mono-200 text-xs font-medium capitalize">
                        {contaminant.type?.replace(/_/g, ' ') || 'Unknown'}
                      </span>
                      <span className="text-mono-300 text-xs font-mono">
                        {(concUgL / 1000).toFixed(3)} mg/L
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-mono-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${exceedance > 1 ? 'bg-gradient-to-r from-yellow-500 to-red-500' : 'bg-green-500'}`}
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <span className={`text-[10px] font-mono ${exceedanceColor}`}>
                        {exceedance > 1 ? `${exceedance.toFixed(1)}x MCL` : 'Below MCL'}
                      </span>
                    </div>
                    <div className="text-[10px] text-mono-600 mt-1">
                      MCL: {mcl} &micro;g/L
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </FadeIn>
  );
}
